import React, { useEffect } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { CircularProgress, ThemeProvider, Box } from '@material-ui/core';

import sleepioTheme from '@/themes/sleepioTheme';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { activateSleepioConfirm } from '@/utils/sleepioActivationApi';
import { logError } from '@/utils/logError';
import { buildRedirectParam } from '@/utils/urlHelpers';

// The deep link scheme Singular intercepts to return the user to the app.
const NATIVE_CALLBACK = 'sleepio://authCallback';

interface RouteParams {
  lob: string;
}

/**
 * Handles the magic link callback for Sleepio Canopy activation.
 * Route: /sleepio/:lob/activate/email/callback
 *
 * VAF embeds intent in the callback URL: ?flow=login for login, absent for sign-up.
 * Sign-up → activateSleepioConfirm then /coverage; login → /complete directly.
 */
const EmailCallbackInner: React.FC = () => {
  const history = useHistory();
  const { lob } = useParams<RouteParams>();
  const { search } = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(search);
    const orgSlug = params.get('org_slug') || '';
    const isLogin = params.get('flow') === 'login';
    const intent = isLogin ? 'login' : 'sign-up';
    const activationBase = `/activate/sleepio-canopy/${lob}/${orgSlug}/${intent}`;
    const redirectParam = buildRedirectParam(NATIVE_CALLBACK);

    const onError = () => history.replace(`${activationBase}/auth${redirectParam}`);

    const handleCallback = async () => {
      await new Promise((resolve) => { setTimeout(resolve, 1500); });

      const { data: { session } } = await getSupabaseClient().auth.getSession();

      if (!session) {
        logError('[SleepioEmailCallback]', 'No session after magic link');
        onError();
        return;
      }

      if (!isLogin) {
        await activateSleepioConfirm({ accessToken: session.access_token });
        history.replace(`${activationBase}/coverage${redirectParam}`);
      } else {
        history.replace(`${activationBase}/complete${redirectParam}`);
      }
    };

    handleCallback().catch((err) => {
      logError('[SleepioEmailCallback]', 'Callback failed', err);
      onError();
    });
  }, [history, lob, search]);

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
      <CircularProgress />
    </Box>
  );
};

const SleepioEmailCallback: React.FC = () => (
  <ThemeProvider theme={sleepioTheme}>
    <EmailCallbackInner />
  </ThemeProvider>
);

export default SleepioEmailCallback;
